import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/Button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Search,
  MapPin,
  Phone,
  Calendar,
  Video,
  Users,
  Star,
  CheckCircle,
  X,
  SlidersHorizontal
} from 'lucide-react';
import { FetchPublicProviders, GetCategory } from '../../../api/helper';


const insuranceOptions = [
  "Medicare", "Medicaid", "Blue Cross Blue Shield", "Aetna", "UnitedHealthcare",
  "Cigna", "Humana", "TRICARE", "Kaiser Permanente", "Molina Healthcare"
];
const languageOptions = ["English", "Spanish", "Mandarin", "Portuguese", "French", "German"];
const ageGroupOptions = ["Children", "Adolescents", "Adults", "Seniors"];
const treatmentApproachOptions = [
  "CBT", "DBT", "Psychotherapy", "Medication Management", "Family Therapy",
  "Group Therapy", "EMDR", "Mindfulness-Based", "Psychodynamic"
];

export const ProviderDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
  const [selectedTreatmentApproaches, setSelectedTreatmentApproaches] = useState([]);
  const [acceptingNewPatients, setAcceptingNewPatients] = useState(false);
  const [telehealthOnly, setTelehealthOnly] = useState(false);
  const [maxDistance, setMaxDistance] = useState(25);
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);
  const [providerdata, setproviderdata] = useState([])
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [paginationTotal, setPaginationTotal] = useState(0);
  const [specialtydata, setspecialtydata] = useState([]);
  const [debouncedLocation, setDebouncedLocation] = useState(locationFilter);
  const navigate = useNavigate()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLocation(locationFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [locationFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setLocationFilter('');
    setSelectedSpecializations([]);
    setSelectedInsurance([]);
    setSelectedLanguages([]);
    setSelectedAgeGroups([]);
    setSelectedTreatmentApproaches([]);
    setAcceptingNewPatients(false);
    setTelehealthOnly(false);
    setMaxDistance(25);
    setSortBy('rating');
  };

  const activeFilterCount = [
    selectedSpecializations,
    selectedInsurance,
    selectedLanguages,
    selectedAgeGroups,
    selectedTreatmentApproaches
  ].reduce((count, filter) => count + filter.length, 0) +
    (acceptingNewPatients ? 1 : 0) +
    (telehealthOnly ? 1 : 0);

  const handleMultiSelectChange = (id, currentSelection, setter) => {
    if (currentSelection.includes(id)) {
      setter(currentSelection.filter(item => item !== id));
    } else {
      setter([...currentSelection, id]);
    }
  }
  const GetProvder = async (page = 1, search = '', location = '') => {
    try {
      setLoadingMore(true);

      const payload = {
        page,
        limit: 10,
        search,
        location,
        ...(selectedSpecializations.length > 0 && { categoryId: selectedSpecializations }),
      };
      const respo = await FetchPublicProviders(payload);
      const fetchedData = respo?.data?.data?.data || [];
      const pagination = respo?.data?.data?.pagination;

      setPaginationTotal(pagination.total || 0);

      if (page === 1) {
        setproviderdata(fetchedData);
      } else {
        setproviderdata(prev => [...prev, ...fetchedData]);
      }

      setCurrentPage(pagination.page);
      setTotalPages(pagination.totalPages);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    GetProvder(1, debouncedSearchTerm, debouncedLocation);
  }, [debouncedSearchTerm, selectedSpecializations, debouncedLocation]);


  function getNextAvailableInDaysFromDate(dateStr) {
    try {
      const now = new Date();
      const availableDate = new Date(dateStr);
      const diffTime = availableDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Within ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } catch {
      return "not available";
    }
  }

  function getNextAvailableInDays(availableDays) {
    if (!Array.isArray(availableDays) || availableDays.length === 0) {
      return "not available";
    }

    const daysOfWeek = [
      "Sunday", "Monday", "Tuesday", "Wednesday",
      "Thursday", "Friday", "Saturday"
    ];

    const today = new Date();
    const todayIndex = today.getDay(); // 0-6 (Sun-Sat)

    const deltas = availableDays.map((dayObj) => {
      const index = daysOfWeek.indexOf(dayObj.day);
      if (index === -1) return 7;
      let delta = index - todayIndex;
      if (delta < 0) delta += 7;
      return delta;
    });

    const minDelta = Math.min(...deltas);

    return `Within ${minDelta} day${minDelta !== 1 ? 's' : ''}`;
  }

  const getSpecialty = async () => {
    try {
      const resposne = await GetCategory();
      setspecialtydata(resposne?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getSpecialty()
  }, [])
  const handleProviderSelect = (provider) => {
    navigate('/booking/telehealth', { state: { providerdata: provider } });
  };
  // console.log(specialtydata,"specialtydata=-=-")
  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="icon-container icon-container-2xl icon-medical">
              <Users className="icon-4xl" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-clinic-gray-900 mb-6">
            Find Your Mental Health Provider
          </h1>
          <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
            Connect with board-certified psychiatrists and mental health professionals
            who accept your insurance and meet your specific needs.
          </p>
        </div>

        <Card className="card-elevated mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label htmlFor="search">Search Providers</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-clinic-gray-400 w-4 h-4" />
                  <Input
                    id="search"
                    placeholder="Provider name or specialization..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 input-medical"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-clinic-gray-400 w-4 h-4" />
                  <Input
                    id="location"
                    placeholder="City, state, or zip code..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 input-medical"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="sortBy">Sort By</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="input-medical">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="distance">Closest</SelectItem> */}
                    <SelectItem value="experience">Most Experienced</SelectItem>
                    <SelectItem value="nextAvailable">Soonest Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="btn-md"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Advanced Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="text-clinic-gray-600 hover:text-clinic-gray-800"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear All Filters
                </Button>
              )}
            </div>

            {showFilters && (
              <div className="mt-6 pt-6 border-t border-clinic-gray-200">
                <Tabs defaultValue="specialization" className="w-full">
                  <TabsList className="grid w-full grid-cols-6">
                    <TabsTrigger value="specialization">Specialization</TabsTrigger>
                    <TabsTrigger value="insurance">Insurance</TabsTrigger>
                    <TabsTrigger value="language">Language</TabsTrigger>
                    <TabsTrigger value="age">Age Group</TabsTrigger>
                    <TabsTrigger value="treatment">Treatment</TabsTrigger>
                    <TabsTrigger value="availability">Availability</TabsTrigger>
                  </TabsList>

                  <TabsContent value="specialization" className="mt-4">
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {specialtydata && specialtydata.length > 0 ? (
                        specialtydata.map((spec) => (
                          <div key={spec._id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`spec-${spec._id}`}
                              checked={selectedSpecializations.includes(spec._id)}
                              onCheckedChange={() => handleMultiSelectChange(spec._id, selectedSpecializations, setSelectedSpecializations)}
                            />
                            <Label htmlFor={`spec-${spec._id}`} className="text-sm">{spec.name}</Label>
                          </div>
                        ))
                      ) : (
                        <p>No specializations found.</p>
                      )}

                    </div>
                  </TabsContent>


                  <TabsContent value="insurance" className="mt-4">
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {insuranceOptions.map((insurance) => (
                        <div key={insurance} className="flex items-center space-x-2">
                          <Checkbox
                            id={`ins-${insurance}`}
                            checked={selectedInsurance.includes(insurance)}
                            onCheckedChange={() => handleMultiSelectChange(insurance, selectedInsurance, setSelectedInsurance)}
                          />
                          <Label htmlFor={`ins-${insurance}`} className="text-sm">{insurance}</Label>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="language" className="mt-4">
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {languageOptions.map((language) => (
                        <div key={language} className="flex items-center space-x-2">
                          <Checkbox
                            id={`lang-${language}`}
                            checked={selectedLanguages.includes(language)}
                            onCheckedChange={() => handleMultiSelectChange(language, selectedLanguages, setSelectedLanguages)}
                          />
                          <Label htmlFor={`lang-${language}`} className="text-sm">{language}</Label>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="age" className="mt-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {ageGroupOptions.map((age) => (
                        <div key={age} className="flex items-center space-x-2">
                          <Checkbox
                            id={`age-${age}`}
                            checked={selectedAgeGroups.includes(age)}
                            onCheckedChange={() => handleMultiSelectChange(age, selectedAgeGroups, setSelectedAgeGroups)}
                          />
                          <Label htmlFor={`age-${age}`} className="text-sm">{age}</Label>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="treatment" className="mt-4">
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {treatmentApproachOptions.map((treatment) => (
                        <div key={treatment} className="flex items-center space-x-2">
                          <Checkbox
                            id={`treat-${treatment}`}
                            checked={selectedTreatmentApproaches.includes(treatment)}
                            onCheckedChange={() => handleMultiSelectChange(treatment, selectedTreatmentApproaches, setSelectedTreatmentApproaches)}
                          />
                          <Label htmlFor={`treat-${treatment}`} className="text-sm">{treatment}</Label>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="availability" className="mt-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="new-patients"
                            checked={acceptingNewPatients}
                            onCheckedChange={(checked) => setAcceptingNewPatients(checked)}
                          />
                          <Label htmlFor="new-patients">Accepting New Patients</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="telehealth"
                            checked={telehealthOnly}
                            onCheckedChange={(checked) => setTelehealthOnly(checked)}
                          />
                          <Label htmlFor="telehealth">Telehealth Available</Label>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="distance">Maximum Distance: {maxDistance} miles</Label>
                        <input
                          type="range"
                          id="distance"
                          min="1"
                          max="50"
                          value={maxDistance}
                          onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                          className="w-full mt-2"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mb-6">
          <p className="text-clinic-gray-600">
            Showing {providerdata.length} of {paginationTotal} providers
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {providerdata?.map((provider) => {
            // console.log(provider,"provider====")
            return (
              <Card key={provider?._id} className="card-elevated card-interactive">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {provider?.image ? (
                      <img
                        src={provider.image}
                        alt={`${provider?.firstName} ${provider?.lastName}`}
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    ) : (
                      <Users className="w-24 h-24 rounded-lg object-cover text-gray-400 bg-gray-100 p-4" />
                    )}

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-xl font-semibold text-clinic-gray-900">
                            {provider?.firstName || ''} {provider?.lastName || ''}
                          </h3>
                          <p className="text-clinic-gray-600">{provider?.title || 'Provider'}</p>
                          <p className="text-sm text-clinic-gray-500">{provider?.LicenseType || 'Not specified'}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 mb-1">
                            <Star className="w-4 h-4 fill-current text-yellow-400" />
                            <span className="text-sm font-medium">{provider?.rating || '4.5'}</span>
                            <span className="text-xs text-clinic-gray-500">
                              ({provider?.reviewCount || '12'})
                            </span>
                          </div>
                          <p className="text-xs text-clinic-gray-500">
                            {typeof provider?.experience === 'string' ? provider.experience : '1-3'} years exp.
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex flex-wrap gap-1">
                          {provider?.Specialties?.slice(0, 3).map((spec) => (
                            <Badge key={spec?._id} variant="secondary" className="text-xs">
                              {spec?.name}
                            </Badge>
                          ))}
                          {provider?.Specialties?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{provider?.Specialties.length - 3} more
                            </Badge>
                          )}
                          {!provider?.Specialties?.length && (
                            <Badge variant="outline" className="text-xs">
                              General Practice
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mb-3 text-sm text-clinic-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{provider?.practiceLocation || provider?.address?.city || 'Unknown Location'}</span>
                        </div>
                        <span>•</span>
                        <span>{provider?.distance || '5'} miles away</span>
                      </div>

                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">Accepting New Patients</span>
                        </div>
                        <div className="flex items-center gap-1 text-clinic-blue-600">
                          <Video className="w-4 h-4" />
                          <span className="text-sm">Telehealth</span>
                        </div>
                      </div>

                      <div className="text-sm text-clinic-gray-600 mb-4">
                        <p>
                          <span className="font-medium">Languages:</span>{" "}
                          {provider?.LanguagesId?.map((lang) => lang?.name).join(", ") || 'English'}
                        </p>
                        <p>
                          <span className="font-medium">Insurance:</span>{" "}
                          {provider?.acceptedInsurance?.slice(0, 2).join(", ") || 'Self-pay, Medicare'}
                          {provider?.acceptedInsurance?.length > 2 &&
                            ` +${provider.acceptedInsurance.length - 2} more`}
                        </p>
                        <p>
                          <span className="font-medium">Next Available:</span>{" "}
                          {provider?.nextAvailable
                            ? getNextAvailableInDaysFromDate(provider.nextAvailable)
                            : getNextAvailableInDays(provider?.availableDays?.availableDays)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {/* <Button className="btn-md_two flex-1" asChild> 
                  <Link to="/booking/telehealth" state={{ providerdata: provider }}>
                   <Calendar className="w-4 h-4 mr-2" /> Book Appointment
                    </Link> 
                    </Button> */}
                    <Button
                      className="btn-md_two flex-1"
                      onClick={() => handleProviderSelect(provider)}
                    >
                      Book Appointment
                    </Button>
                    <Button variant="outline" className="btn-md" asChild>
                      <Link to="/contact">
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </Link>
                    </Button>
                    <Button variant="outline" className="btn-md">
                      <Link to="/Provider-View-Details" state={{ providerId: provider?._id }}>
                        View Profile
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}

        </div>
        {currentPage < totalPages && (
          <div className="flex justify-center mt-6">
            <Button onClick={() => GetProvder(currentPage + 1)} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load More Providers'}
            </Button>
          </div>
        )}


        {providerdata?.length === 0 && (
          <Card className="text-center p-12">
            <Search className="mx-auto h-16 w-16 text-clinic-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">No providers found</h3>
            <p className="text-clinic-gray-600 mb-4">
              Try adjusting your search criteria or clearing some filters to see more results.
            </p>
            <Button onClick={clearAllFilters} variant="outline" className="btn-md">
              Clear All Filters
            </Button>
          </Card>
        )}

      </div>
    </div>
  );
};